<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { FileText, ExternalLink, ArrowLeft } from 'lucide-vue-next'
import { toast } from '@/utils/toast'
import { tenantApi } from '@/services/api'
import type { Template } from '@/types'

const { t } = useI18n()
const router = useRouter()

interface TemplateWithBase extends Template {
  lark_base_token?: string
}

const templates = ref<TemplateWithBase[]>([])

// Load all templates for the tenant
async function loadTemplates(): Promise<void> {
  try {
    const result = await tenantApi.getTenantTemplates()
    templates.value = result.templates
  } catch (error) {
    console.error('Failed to load templates:', error)
    toast.error(error instanceof Error ? error.message : t('failedToLoadData'))
  }
}

// Generate Lark Table URL (universal format works for all tenants)
function getLarkTableUrl(baseToken: string, tableId: string): string {
  return `https://larksuite.com/base/${baseToken}?table=${tableId}`
}

// Open in Lark
function openInLark(template: TemplateWithBase): void {
  if (template.lark_base_token) {
    const url = getLarkTableUrl(template.lark_base_token, template.lark_table_id)
    window.open(url, '_blank')
  }
}

onMounted(() => {
  loadTemplates()
})
</script>

<template>
  <div class="h-full bg-white flex flex-col overflow-hidden">
    <div class="flex-1 overflow-y-auto">
      <div class="max-w-6xl mx-auto w-full p-8">
        <!-- Header -->
        <div class="mb-6">
          <button
            @click="router.push('/my-company')"
            class="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft :size="16" />
            {{ t('myCompany') }}
          </button>
        </div>

        <!-- Empty State -->
        <div v-if="templates.length === 0" class="text-center py-12">
          <FileText :size="64" class="mx-auto text-gray-300 mb-4" />
          <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ t('noTemplatesFound') }}</h3>
          <p class="text-gray-600 mb-6">{{ t('noTemplatesMessage') }}</p>
          <button
            @click="router.push('/template-setup')"
            class="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            {{ t('goToTemplateSetup') }}
          </button>
        </div>

        <!-- Templates Table -->
        <div v-else class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {{ t('templateName') }}
                  </th>
                  <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {{ t('baseTable') }}
                  </th>
                  <th class="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {{ t('actions') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr
                  v-for="template in templates"
                  :key="template.id"
                  class="hover:bg-gray-50 transition-colors"
                >
                  <!-- Template Name -->
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-3">
                      <FileText :size="18" class="text-blue-600 flex-shrink-0" />
                      <span class="text-sm font-medium text-gray-900" :title="template.name">
                        {{ template.name }}
                      </span>
                    </div>
                  </td>

                  <!-- Base / Table -->
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm">
                      <div class="text-gray-900 font-mono text-xs">{{ template.lark_base_token || '-' }}</div>
                      <div class="text-gray-500 font-mono text-xs mt-1">{{ template.lark_table_id }}</div>
                    </div>
                  </td>

                  <!-- Actions -->
                  <td class="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      v-if="template.lark_base_token"
                      @click="openInLark(template)"
                      class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <ExternalLink :size="16" />
                      {{ t('openInLark') }}
                    </button>
                    <span v-else class="text-sm text-gray-400">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
